import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Image, Input, Spin, message } from 'antd';
import {
  ArrowRightOutlined,
  LogoutOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { useConnection, useUser } from '@sezenta/connection';
import {
  Contract,
  getRefreshTokenCookie,
  isSuperAdmin,
  setLastContractId,
  useContracts,
} from '../../context/ContractHelpers';

// CR004: the single hub that decides where a signed-in user lands.
//  0 contracts → super-admin to /admin, else "no access".
//  1 contract  → auto-enter (zero friction, preserves single-tenant UX).
//  >1          → branded picker.
const SelectContract: React.FC = () => {
  const { contracts, loading } = useContracts();
  const connection = useConnection();
  const user = useUser();
  const navigate = useNavigate();
  const [cookies] = useCookies(['refreshToken']);
  const [search, setSearch] = useState('');
  const [entering, setEntering] = useState<string | null>(null);

  const enter = async (id: string) => {
    // Web-only projects the user can't enter (e.g. they're just a driver there)
    // are shown disabled; ignore an attempt to open one.
    const target = contracts.find((c) => c.businessId === id);
    if (target && target.webAccess === false) {
      message.info('You only have field (mobile) access to this project.');
      return;
    }
    setEntering(id);
    try {
      // Re-scope the token only when the chosen contract differs from the one the
      // current token already carries (the login token is scoped to '1'). Avoids a
      // redundant switch (and the lagging refresh cookie) for the common case.
      if ((user as any)?.businessId !== id) {
        const sw = await connection.post(`auth/switch-contract/${id}`);
        // Adopt the NEW contract-scoped refresh token — useUser()/permissions are
        // derived from the refresh token, so keeping the old one would leave the
        // user on the previous project's permissions.
        connection.login(
          sw.data.accessToken,
          sw.data.refreshToken ??
            getRefreshTokenCookie() ??
            cookies.refreshToken,
        );
      }
      setLastContractId(id);
      navigate(`/portal/${id}/dashboard`);
    } catch (e) {
      setEntering(null);
      message.error('Could not open that project');
    }
  };

  // Must be signed in to choose a contract. Guard on the access-token user (like
  // NewMainLayout) — `undefined` while loading won't bounce, only a definitive
  // logged-out `null` does. (The refreshToken cookie lags right after login.)
  useEffect(() => {
    if (user === null) navigate('/login');
  }, [user, navigate]);

  // Auto-decisions once contracts resolve.
  useEffect(() => {
    if (loading) return;
    if (contracts.length === 0) {
      if (isSuperAdmin(user)) navigate('/admin/projects');
      return; // else fall through to the "no access" empty state
    }
    // Auto-enter a lone project only if the user can actually use it on web.
    if (contracts.length === 1 && contracts[0].webAccess !== false) {
      enter(contracts[0].businessId);
    }
    // >1 (or a single no-web-access project) → render the picker below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, contracts.length]);

  const filtered = useMemo(
    () =>
      contracts.filter((c) =>
        (c.name ?? c.businessId).toLowerCase().includes(search.toLowerCase()),
      ),
    [contracts, search],
  );

  const onLogout = () => {
    connection.logout();
    navigate('/login');
  };

  // Loading / auto-forwarding states.
  const autoEntering =
    !loading && contracts.length === 1 && contracts[0]?.webAccess !== false;
  if (loading || autoEntering || entering) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-[#F7F9FB]">
        <Spin
          size="large"
          tip={entering ? 'Opening project…' : 'Loading your projects…'}
        />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#F7F9FB] flex flex-col">
      {/* Top bar */}
      <div
        className="w-full h-[64px] px-8 flex items-center bg-white"
        style={{ boxShadow: '0px 1px 0px rgba(18,32,59,0.09)' }}
      >
        <Image src="/img/logo.png" preview={false} width={120} />
        <button
          onClick={onLogout}
          className="ml-auto flex items-center gap-2 text-[#5E6870] hover:text-[#26A3A7] bg-transparent border-0 cursor-pointer text-[14px]"
        >
          <LogoutOutlined /> Log out
        </button>
      </div>

      <div className="flex-1 w-full max-w-[1040px] mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="text-[13px] font-medium text-[#26A3A7] uppercase tracking-wide mb-1">
            {(user as any)?.firstName
              ? `Welcome back, ${(user as any).firstName}`
              : 'Welcome back'}
          </div>
          <h1 className="m-0 text-[30px] font-semibold text-[#18191C] leading-tight">
            Select a project
          </h1>
          <p className="text-[#5E6870] text-[15px] mt-2 mb-0">
            You have access to {contracts.length}{' '}
            {contracts.length === 1 ? 'project' : 'projects'}. Choose one to
            continue.
          </p>
        </div>

        {contracts.length > 6 && (
          <Input
            allowClear
            size="large"
            prefix={<SearchOutlined className="text-[#84919A]" />}
            placeholder="Search projects"
            className="mb-7 max-w-[380px] rounded-lg"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}

        {filtered.length === 0 ? (
          <div className="bg-white rounded-[14px] border border-[#EAECF0] py-16">
            <Empty description="No projects match your search" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((c) => (
              <ContractCard
                key={c.businessId}
                contract={c}
                entering={entering === c.businessId}
                onOpen={enter}
              />
            ))}
          </div>
        )}
      </div>

      <div className="w-full text-center text-[12px] text-[#A8B0B8] pb-6">
        Southern Cross Recycling Group
      </div>
    </div>
  );
};

// Deterministic accent per project so the grid reads as distinct cards (color is
// derived from the id, so it's stable across renders).
const ACCENTS: [string, string][] = [
  ['#E6F4F4', '#1A8B8F'],
  ['#EEF2FF', '#4F46E5'],
  ['#FFF4E6', '#B7791F'],
  ['#ECFDF3', '#039855'],
  ['#FCE7F6', '#C026D3'],
  ['#FEF3F2', '#C0392B'],
];
const accentFor = (key: string): [string, string] => {
  const sum = [...key].reduce((a, c) => a + c.charCodeAt(0), 0);
  return ACCENTS[sum % ACCENTS.length];
};

// A single project choice — branded card, not a plain list row.
const ContractCard: React.FC<{
  contract: Contract;
  entering?: boolean;
  onOpen: (id: string) => void;
}> = ({ contract, entering, onOpen }) => {
  const name = contract.name ?? contract.businessId;
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  const inactive = contract.status === false;
  // The user belongs to this project but only with field (mobile) access — show
  // it, but disabled, so they understand why they can't open it on web.
  const noWebAccess = contract.webAccess === false;
  const disabled = inactive || noWebAccess;
  const [bg, fg] = accentFor(contract.businessId);
  return (
    <button
      disabled={disabled || entering}
      onClick={() => onOpen(contract.businessId)}
      title={
        noWebAccess
          ? 'You only have field (mobile) access to this project'
          : undefined
      }
      className={`group relative w-full text-left bg-white rounded-[14px] p-5 border border-[#EAECF0] transition-all duration-200 cursor-pointer disabled:cursor-not-allowed ${
        disabled
          ? 'opacity-60'
          : 'hover:border-[#26A3A7] hover:-translate-y-1 hover:shadow-[0_14px_28px_-10px_rgba(16,24,40,0.18)]'
      }`}
      style={{ boxShadow: '0px 1px 2px rgba(16,24,40,0.05)' }}
    >
      <div className="flex items-start gap-3 mb-5">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center text-[20px] font-semibold shrink-0"
          style={{ background: bg, color: fg }}
        >
          {entering ? <Spin size="small" /> : initial}
        </div>
        <div className="flex-1 min-w-0 pt-0.5">
          <div className="text-[16px] font-semibold text-[#18191C] truncate">
            {name}
          </div>
          <div className="mt-1.5">
            {inactive ? (
              <span className="inline-flex items-center gap-1.5 text-[12px] text-[#C0392B]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C0392B]" />
                Inactive
              </span>
            ) : noWebAccess ? (
              <span className="inline-flex items-center gap-1.5 text-[12px] text-[#B7791F]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B7791F]" />
                Mobile access only
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[12px] text-[#5E6870]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#12B76A]" />
                Active
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-[#F0F1F3] pt-3">
        <span className="text-[13px] text-[#84919A] group-hover:text-[#26A3A7] transition-colors">
          {entering
            ? 'Opening…'
            : inactive
              ? 'Unavailable'
              : noWebAccess
                ? 'No web access'
                : 'Open project'}
        </span>
        <span className="w-7 h-7 rounded-full flex items-center justify-center text-[#26A3A7] bg-[#E6F4F4] transition-colors group-hover:bg-[#26A3A7] group-hover:text-white">
          <ArrowRightOutlined />
        </span>
      </div>
    </button>
  );
};

export default SelectContract;
